<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { infoBoxIdEnum } from '@/stores/portal.store'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import InfoBox from '@/components/shared/InfoBox.vue'
import ExpenseItemEdit from '@/components/accounting/ExpenseItemEdit.vue'
import ExpenseItemAvatar from '@/components/accounting/ExpenseItemAvatar.vue'

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const accountingStore = useAccountingStore()
const webSiteStore = useWebSiteStore()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const canEdit = webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS)
const container = ref(null)
const editItem = ref(null)

const expenseItems = computed(() => accountingStore.getLocalizedCostReportRowTags())
const unitOfMeasureById = computed(() => new Map(webSiteStore.getLocalizedUnitsOfMeasure().map(unit => [unit.id, unit])))

const itemCaption = (item) => [
  unitOfMeasureById.value.get(item.unit_of_measure_ref)?.name,
  i18n.t(item.receipt_required ? 'common.expenseItemReceiptRequired' : 'common.expenseItemReceiptOptional'),
].filter(Boolean).join(' · ')

const onEdit = (item) => {
  if (canEdit) editItem.value = item
}

const onItemSaved = async(saved) => {
  const createdId = editItem.value?.id ? null : saved?.id
  editItem.value = null
  if (createdId) await highlightRowAsync(createdId, container)
}

onMounted(() => accountingStore.fetchCostReportRowTagsAsync({}, true))
</script>

<template>
  <div ref="container" class="expense-item-settings">
    <div v-if="canEdit" class="row justify-end items-center q-pb-sm" :class="{ 'q-px-md': isMobile }">
      <q-btn
        style="padding: 0;"
        size="md"
        unelevated
        ripple
        rounded
        icon="mdi-plus-circle-outline"
        @click="editItem = {}"
      >
        <q-tooltip>{{ i18n.t('common.expenseItemNew') }}</q-tooltip>
      </q-btn>
    </div>
    <q-list bordered separator class="rounded-borders">
      <q-item
        v-for="item in expenseItems"
        :key="item.id"
        class="q-py-sm"
        :class="{ 'row-highlight': item.id === highlightedRowId }"
        :clickable="canEdit"
        @click="onEdit(item)"
      >
        <q-item-section avatar>
          <expense-item-avatar :item="item" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-weight-medium">{{ item.name }}</q-item-label>
          <q-item-label caption>
            <q-icon v-if="item.receipt_required" name="mdi-paperclip" />
            {{ itemCaption(item) }}
          </q-item-label>
        </q-item-section>
        <q-item-section v-if="canEdit" side>
          <q-btn
            flat
            round
            dense
            size="sm"
            icon="mdi-pencil"
            @click.stop="onEdit(item)"
          >
            <q-tooltip>{{ i18n.t('common.edit') }}</q-tooltip>
          </q-btn>
        </q-item-section>
      </q-item>
      <q-item v-if="!expenseItems.length">
        <q-item-section class="text-grey-7">{{ i18n.t('common.noExpenseItems') }}</q-item-section>
      </q-item>
    </q-list>
    <info-box :id="infoBoxIdEnum.EXPENSE_ITEMS" :class="isMobile ? 'q-ma-md' : 'q-mt-md'" :message="i18n.t('info.expenseItemsHint')" />

    <q-dialog
      :model-value="editItem !== null"
      class="dialog-xs"
      :maximized="isMobile"
      @hide="editItem = null"
    >
      <expense-item-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onItemSaved"
        @cancel="editItem = null"
      />
    </q-dialog>
  </div>
</template>

<style scoped>
.expense-item-settings {
  max-width: 40rem;
}
</style>
